import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useComfyUIEndpoint } from '@/hooks/useComfyUIEndpoint';
import { Settings, Plug, CheckCircle, XCircle, Loader2 } from 'lucide-react';

export default function SettingsPage() {
  const { endpoint, loading, saving, testing, testResult, error, saveEndpoint, testConnection } = useComfyUIEndpoint();

  const [url, setUrl] = useState('');
  const [apiKey, setApiKey] = useState('');

  useEffect(() => {
    if (endpoint) {
      setUrl(endpoint.url || '');
      setApiKey(endpoint.apiKey || '');
    }
  }, [endpoint]);

  const handleTest = async () => {
    if (!url.trim()) return;
    await testConnection({ url: url.trim(), ...(apiKey ? { apiKey } : {}) });
  };

  const handleSave = async () => {
    if (!url.trim()) return;
    await saveEndpoint({ url: url.trim(), ...(apiKey ? { apiKey } : {}) });
  };

  if (loading) {
    return (
      <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100 p-8 flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="h-full bg-gradient-to-br from-slate-50 to-slate-100 p-8">
      <div className="max-w-2xl mx-auto">
        <header className="mb-8">
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Settings
          </h1>
          <p className="text-sm text-slate-600 mt-1">Admin: Configure your ComfyUI server connection</p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plug className="h-5 w-5" />
              ComfyUI Server
            </CardTitle>
            <CardDescription>
              Enter the URL of your remote ComfyUI instance. The server must be reachable from the Firebase backend.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="comfyui-url">Server URL</Label>
              <Input
                id="comfyui-url"
                type="url"
                placeholder="http://comfyui.example.com:8188"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">The full URL including port (e.g., http://your-server:8188)</p>
              <p className="text-xs text-destructive font-medium mt-1">
                Warning: This must point to a valid ComfyUI instance. Changing this incorrectly will break all workflow
                executions.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="comfyui-apikey">API Key (optional)</Label>
              <Input
                id="comfyui-apikey"
                type="password"
                placeholder="Enter API key if required"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Only needed if your ComfyUI instance requires authentication</p>
            </div>

            {testResult && (
              <div
                className={`p-3 rounded-md border ${
                  testResult.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                }`}
              >
                <div className="flex items-center gap-2">
                  {testResult.success ? (
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  ) : (
                    <XCircle className="h-4 w-4 text-red-600" />
                  )}
                  <span className={`text-sm font-medium ${testResult.success ? 'text-green-700' : 'text-red-700'}`}>
                    {testResult.success ? 'Connection successful' : 'Connection failed'}
                  </span>
                </div>
                {testResult.error && <p className="text-xs text-red-600 mt-1">{testResult.error}</p>}
                {testResult.success && testResult.stats && (
                  <p className="text-xs text-green-600 mt-1">ComfyUI server is running and responding</p>
                )}
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            )}

            <div className="flex gap-3">
              <Button variant="outline" onClick={handleTest} disabled={!url.trim() || testing}>
                {testing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Testing...
                  </>
                ) : (
                  'Test Connection'
                )}
              </Button>
              <Button onClick={handleSave} disabled={!url.trim() || saving}>
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </Button>
            </div>

            {endpoint?.updatedAt && (
              <p className="text-xs text-muted-foreground">Last saved: {new Date(endpoint.updatedAt).toLocaleString()}</p>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
